// Desktop Dev Chat Manager
// Centralizes API calls for models, load/unload - UI holds React state and calls these methods

import { Manager, logger } from '../../../../../core';

export class DesktopDevChatManager extends Manager {
  constructor() {
    super();
    this.isInitialized = false;
    this.models = [];
  }

  /**
   * Initialize states
   */
  async initStates() {
    logger.log(
      { tags: 'manager|desktop|dev-chat|init', color1: 'cyan' },
      'Initializing Desktop Dev Chat manager'
    );

    await this.loadModels();
    this.isInitialized = true;

    logger.log(
      { tags: 'manager|desktop|dev-chat|ready', color1: 'green' },
      `Desktop Dev Chat manager initialized with ${this.models.length} models`
    );
  }

  /**
   * Fetch models from Desktop models API (file-mode only for loading)
   */
  async loadModels() {
    try {
      if (typeof window !== 'undefined' && window.modelsAPI) {
        this.models = await window.modelsAPI.getAll();
      } else {
        this.models = [];
      }
    } catch (error) {
      logger.warn(
        { tags: 'manager|desktop|dev-chat|models', color1: 'yellow' },
        'Failed to load models:',
        error
      );
      this.models = [];
    }
    return this.models;
  }

  /**
   * Get models suitable for dropdown (file-mode with path)
   */
  getModels() {
    return this.models.filter((m) => m.mode === 'file' && m.path);
  }

  /**
   * Get model by id
   */
  getModelById(modelId) {
    return this.models.find((m) => m.id === modelId) || null;
  }

  /**
   * Load a model by id. Call after subscribing to onLoadProgress.
   * @param {string} modelId
   * @param {Object} [options] - { contextSize, contextAllocationSize?, defaultContextFlashAttention? }
   * Returns IPC result from load; after success, use isModelLoaded() for modelInfo.contextSize (resolved tokens).
   */
  async loadModel(modelId, options = {}) {
    const model = this.getModelById(modelId);
    if (!model || !model.path) {
      return { success: false, error: 'Model not found or has no path' };
    }

    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.loadModel) {
      return { success: false, error: 'Dev Chat API not available' };
    }

    const opts = {
      contextSize: options.contextSize ?? 'auto',
      contextAllocationSize: options.contextAllocationSize ?? undefined,
      defaultContextFlashAttention: options.defaultContextFlashAttention,
    };
    const result = await window.llamaCppDevAPI.loadModel(model.path, opts);
    return result;
  }

  /**
   * Unload the current model.
   * Returns { success, error? }
   */
  async unloadModel() {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.unloadModel) {
      return { success: false, error: 'Dev Chat API not available' };
    }
    const result = await window.llamaCppDevAPI.unloadModel();
    return result;
  }

  /**
   * Check if a model is loaded. Returns { success, isLoaded, modelInfo? }
   */
  async isModelLoaded() {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.isModelLoaded) {
      return { success: false, isLoaded: false };
    }
    return window.llamaCppDevAPI.isModelLoaded();
  }

  /**
   * Subscribe to load progress (returns cleanup function)
   */
  onLoadProgress(callback) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.onLoadProgress) {
      return () => {};
    }
    return window.llamaCppDevAPI.onLoadProgress(callback);
  }

  /**
   * Generate a response from the loaded model.
   * Subscribe to onGenerateStream before calling to receive text/segment events.
   *
   * @param {string} prompt - User message
   * @param {Object} [options] - { systemPrompt, grammar, jsonSchema, functions, temperature, topK, topP, seed, maxTokens, responsePrefix, streamSegments, budgets, repeatPenalty, chatKnobs? } (chatKnobs exists for future Jinja template prefs; Dev Chat UI does not pass it)
   * @returns {Promise<{ success: boolean, fullResponse?: string, error?: string }>}
   */
  async generate(prompt, options = {}) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.generate) {
      return { success: false, error: 'Dev Chat API not available' };
    }
    return window.llamaCppDevAPI.generate(prompt, options);
  }

  /**
   * Cancel the current generation.
   */
  async cancelGenerate() {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.cancelGenerate) {
      return { success: false };
    }
    return window.llamaCppDevAPI.cancelGenerate();
  }

  /**
   * Subscribe to generate stream events (returns cleanup function).
   * Payload types:
   * - response-chunk: { chunkType, segmentType, text, isSegmentStart, isSegmentEnd, segmentStartTime?, segmentEndTime? }
   * - function-call-params-chunk / tool-call-executing / tool-call-complete / done / error
   */
  onGenerateStream(callback) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.onGenerateStream) {
      return () => {};
    }
    return window.llamaCppDevAPI.onGenerateStream(callback);
  }

  /**
   * Subscribe to backend proxy tool-dispatch events.
   * Payload:
   * { runId, callIndex, functionName, params }
   */
  onToolCallDispatch(callback) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.onToolCallDispatch) {
      return () => {};
    }
    return window.llamaCppDevAPI.onToolCallDispatch(callback);
  }

  /**
   * Send the final frontend tool result back to the backend proxy handler.
   * @param {{ runId: string, callIndex: number, ok: boolean, result?: unknown, summary?: string, error?: string }} args
   */
  async toolExecutionComplete({ runId, callIndex, ok, result, summary, error }) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.toolExecutionComplete) {
      return { ok: false, error: 'Dev Chat API not available' };
    }
    return window.llamaCppDevAPI.toolExecutionComplete(runId, callIndex, {
      ok: !!ok,
      result,
      summary: typeof summary === 'string' ? summary : '',
      error: typeof error === 'string' ? error : '',
    });
  }

  /**
   * Get chat history for persistence.
   * @returns {Promise<{ success: boolean, history?: Array }>}
   */
  async getChatHistory() {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.getChatHistory) {
      return { success: false };
    }
    return window.llamaCppDevAPI.getChatHistory();
  }

  /**
   * Restore chat history.
   * @param {Array} history - From getChatHistory
   * @param {Object} [options] - { chatKnobs? } — optional; Dev Chat UI does not pass (see backend chat-knob deprecation)
   * @returns {Promise<{ success: boolean }>}
   */
  async setChatHistory(history, options = {}) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.setChatHistory) {
      return { success: false };
    }
    return window.llamaCppDevAPI.setChatHistory(history, options);
  }

  /**
   * Apply template-level chat prefs (Jinja additionalRenderParameters). Recreates session when kwargs change.
   * Not used by Dev Chat UI (thinking toggle deprecated); kept for IPC/manual testing when upstream is reliable.
   * @param {Object} chatKnobs - e.g. { enableThinking: boolean }
   */
  async applyChatKnobs(chatKnobs = {}) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.applyChatKnobs) {
      return { success: false, error: 'Dev Chat API not available' };
    }
    return window.llamaCppDevAPI.applyChatKnobs(chatKnobs);
  }

  /**
   * Preload a prompt onto context sequence state.
   * @param {string} [prompt] - Empty string preloads history only
   * @returns {Promise<{ success: boolean }>}
   */
  async preloadPrompt(prompt) {
    if (typeof window === 'undefined' || !window.llamaCppDevAPI?.preloadPrompt) {
      return { success: false };
    }
    return window.llamaCppDevAPI.preloadPrompt(prompt);
  }

}
